/**
 * Mini-chat flottant style WhatsApp/Facebook Messenger pour les portails.
 * - Bulles bien différenciées (bleues à droite pour moi, grises à gauche pour l'autre)
 * - Avatar auteur pour messages entrants
 * - Bouton « Charger anciens messages » (pagination avant_id)
 * - Upload image + Ctrl/Cmd+V pour coller image du presse-papiers
 * - Suppression de mes messages (menu contextuel + long-press mobile)
 * - Aperçu images inline dans une lightbox (pas de nouvel onglet)
 * - Marque lus à l'ouverture
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Send, ArrowLeft, Image as ImageIcon, ChevronUp, Paperclip,
  MoreVertical, Trash2, Download, Heart, ThumbsUp, Flame, Smile as SmileIcon,
  Reply, Forward, Mic, Play, Pause, Minus,
} from 'lucide-react';
import {
  useListerMessagesQuery,
  useEnvoyerMessageMutation,
  useMarquerLusMutation,
  useDetailConversationQuery,
  useSupprimerMessageMutation,
  useToggleReactionMutation,
  useSignalerTypingMutation,
  useTypingStatusQuery,
  useTransfererMessageMutation,
  useListerConversationsQuery,
} from '../../services/messagerieChat';
import ChatCheckMarks from './ChatCheckMarks';
import ChatEmojiPicker from './ChatEmojiPicker';
import ChatVoiceRecorder from './ChatVoiceRecorder';

interface Props {
  convId: number | null;
  onClose: () => void;
  isDark?: boolean;
  partenaireLabel?: string;
  /** Index dans la stack (0 = plus à droite). Utilisé pour empiler
   *  plusieurs drawers côte à côte façon Messenger. */
  stackIndex?: number;
  /** true si ce drawer est celui qui reçoit les raccourcis clavier. */
  isFocused?: boolean;
  /** Callback quand l'user clique le drawer (pour le passer en focus). */
  onFocus?: () => void;
  /** Callback pour minimiser (transformer en bulle en bas). */
  onMinimize?: () => void;
}

function fmtHeure(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateShort(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return "Aujourd'hui";
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
}

function initiales(nom: string): string {
  return nom.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase();
}

export const PortailChatDrawer: React.FC<Props> = ({
  convId, onClose, isDark = false, partenaireLabel,
  stackIndex = 0, isFocused = true, onFocus, onMinimize,
}) => {
  const skip = !convId;
  const { data: conv } = useDetailConversationQuery(convId!, { skip });

  // Pagination : `avant_id` = plus ancien id chargé
  const [avantId, setAvantId] = useState<number | undefined>(undefined);
  const [historyStack, setHistoryStack] = useState<any[]>([]);
  const { data: msgs, refetch, isFetching } = useListerMessagesQuery(
    { conv_id: convId!, avant_id: avantId, limit: 30 },
    { skip, pollingInterval: skip || avantId ? 0 : 10000 },
  );
  const [envoyer, { isLoading: sending }] = useEnvoyerMessageMutation();
  const [marquerLus] = useMarquerLusMutation();
  const [supprimerMsg] = useSupprimerMessageMutation();
  const [toggleReaction] = useToggleReactionMutation();
  const [signalerTyping] = useSignalerTypingMutation();
  const [transferer] = useTransfererMessageMutation();
  const { data: typingData } = useTypingStatusQuery(convId!, {
    skip, pollingInterval: skip ? 0 : 4000,
  });
  const { data: mesConvs } = useListerConversationsQuery(undefined, { skip });

  // ── Reply-to state ───────────────────────────────────────────────────
  const [replyTo, setReplyTo] = useState<any>(null);
  // ── Transfert state ──────────────────────────────────────────────────
  const [transferSrc, setTransferSrc] = useState<any>(null);
  const [transferTargets, setTransferTargets] = useState<Set<number>>(new Set());
  const [transferring, setTransferring] = useState(false);
  // ── Enregistrement vocal ─────────────────────────────────────────────
  const [voiceMode, setVoiceMode] = useState(false);
  // ── Typing signal (throttlé) ─────────────────────────────────────────
  const lastTypingRef = useRef<number>(0);
  const emitTyping = () => {
    if (!convId) return;
    const now = Date.now();
    if (now - lastTypingRef.current < 2500) return; // throttle 2.5s
    lastTypingRef.current = now;
    signalerTyping(convId).catch(() => {});
  };
  const [text, setText] = useState('');
  const [attachedImage, setAttachedImage] = useState<File | null>(null);
  const [attachedPreview, setAttachedPreview] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Menu contextuel actif (id du message)
  const [menuOpenFor, setMenuOpenFor] = useState<number | null>(null);
  // Lightbox image
  const [lightbox, setLightbox] = useState<{ url: string; nom?: string } | null>(null);
  // Long-press pour ouvrir le menu contextuel sur mobile
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const myUserId = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) return JSON.parse(raw)?.id ?? null;
    } catch {}
    try {
      const raw = localStorage.getItem('user_data');
      if (raw) return JSON.parse(raw)?.id ?? null;
    } catch {}
    return null;
  }, []);

  // Marquer lu à l'ouverture
  useEffect(() => {
    if (convId) marquerLus(convId).catch(() => {});
  }, [convId, marquerLus]);

  // Reset historique quand on change de conv
  useEffect(() => {
    setAvantId(undefined);
    setHistoryStack([]);
    setMenuOpenFor(null);
    setLightbox(null);
  }, [convId]);

  // Concat old messages when loaded
  useEffect(() => {
    if (!msgs?.results) return;
    if (avantId === undefined) {
      setHistoryStack(msgs.results);
    } else {
      setHistoryStack((prev) => {
        const seen = new Set(prev.map((m: any) => m.id));
        const older = msgs.results.filter((m: any) => !seen.has(m.id));
        return [...older, ...prev];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs?.results]);

  // Scroll bas quand nouveau message
  const lastAppendedIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    const last = historyStack[historyStack.length - 1];
    if (last && last.id !== lastAppendedIdRef.current && avantId === undefined) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      lastAppendedIdRef.current = last.id;
    }
  }, [historyStack, avantId]);

  // Ferme le menu contextuel au clic ailleurs
  useEffect(() => {
    if (menuOpenFor === null) return;
    const onDocClick = () => setMenuOpenFor(null);
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpenFor]);

  // Escape ferme la lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const loadOlder = () => {
    const oldest = historyStack[0];
    if (oldest?.id) setAvantId(oldest.id);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      alert('Seules les images sont supportées via ce bouton.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      alert('Image trop volumineuse (max 5 Mo).');
      return;
    }
    setAttachedImage(f);
    setAttachedPreview(URL.createObjectURL(f));
  };

  // Coller image depuis le presse-papiers (Ctrl+V)
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (!convId) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            setAttachedImage(file);
            setAttachedPreview(URL.createObjectURL(file));
            e.preventDefault();
            break;
          }
        }
      }
    };
    window.addEventListener('paste', onPaste as any);
    return () => window.removeEventListener('paste', onPaste as any);
  }, [convId]);

  const send = async () => {
    if (!convId || (!text.trim() && !attachedImage)) return;
    const contenu = text.trim();
    const fichier = attachedImage;
    const currentReplyTo = replyTo;
    setText('');
    setAttachedImage(null);
    setReplyTo(null);
    if (attachedPreview) { URL.revokeObjectURL(attachedPreview); setAttachedPreview(null); }
    try {
      await envoyer({
        conv_id: convId,
        contenu: contenu || undefined,
        fichier: fichier || undefined,
        reply_to_id: currentReplyTo?.id,
      } as any).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || "Erreur d'envoi");
      setText(contenu);
      setReplyTo(currentReplyTo);
      if (fichier) {
        setAttachedImage(fichier);
        setAttachedPreview(URL.createObjectURL(fichier));
      }
    }
  };

  // Envoi d'un message vocal (Blob depuis MediaRecorder)
  const sendVoice = async (blob: Blob, mime: string, dureeMs: number) => {
    if (!convId) return;
    setVoiceMode(false);
    // Créer un File nommé — l'extension aide certains lecteurs mobiles
    const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([blob], `vocal-${Date.now()}.${ext}`, { type: mime });
    try {
      await envoyer({
        conv_id: convId,
        contenu: `🎤 Message vocal · ${Math.round(dureeMs / 1000)}s`,
        fichier: file,
      } as any).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || "Erreur d'envoi vocal");
    }
  };

  const doTransfert = async () => {
    if (!transferSrc || transferTargets.size === 0) return;
    setTransferring(true);
    try {
      const r = await transferer({
        msg_id: transferSrc.id,
        conv_ids: Array.from(transferTargets),
      }).unwrap();
      setTransferSrc(null);
      setTransferTargets(new Set());
      if (r.errors && r.errors.length > 0) {
        alert(`Transféré vers ${r.forwarded.length}. ${r.errors.length} erreur(s).`);
      }
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur transfert');
    } finally {
      setTransferring(false);
    }
  };

  const removeAttachment = () => {
    setAttachedImage(null);
    if (attachedPreview) { URL.revokeObjectURL(attachedPreview); setAttachedPreview(null); }
  };

  // Réactions rapides sous chaque message. Toggle idempotent côté serveur.
  const handleQuickReaction = async (msgId: number, emoji: string) => {
    setMenuOpenFor(null);
    // Update optimiste : ajouter/retirer localement
    setHistoryStack(prev => prev.map(m => {
      if (m.id !== msgId) return m;
      const reactions = Array.isArray(m.reactions) ? [...m.reactions] : [];
      const idx = reactions.findIndex((r: any) => r.emoji === emoji);
      if (idx >= 0) {
        const r = reactions[idx];
        if (r.moi) {
          const nextUsers = r.users.filter((u: any) => u.user_id !== myUserId);
          if (nextUsers.length === 0) reactions.splice(idx, 1);
          else reactions[idx] = { ...r, users: nextUsers, moi: false };
        } else {
          reactions[idx] = {
            ...r, moi: true,
            users: [...r.users, { user_id: myUserId, user_nom: 'Moi' }],
          };
        }
      } else {
        reactions.push({
          emoji, moi: true,
          users: [{ user_id: myUserId, user_nom: 'Moi' }],
        });
      }
      return { ...m, reactions };
    }));
    try {
      await toggleReaction({ msg_id: msgId, emoji }).unwrap();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
      refetch();
    }
  };

  const handleDeleteMessage = async (id: number) => {
    setMenuOpenFor(null);
    if (!window.confirm('Supprimer ce message ? Cette action est définitive.')) return;
    // Optimiste : marque comme supprimé (backend fait un soft-delete)
    setHistoryStack(prev => prev.map(m =>
      m.id === id ? { ...m, supprime: true, contenu: '', fichier_url: undefined } : m,
    ));
    try {
      await supprimerMsg(id).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Suppression impossible (message trop ancien ou non autorisé)');
      refetch(); // récupère la liste réelle
    }
  };

  const startLongPress = (msgId: number, _mine: boolean) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      setMenuOpenFor(msgId);
      try { (navigator as any).vibrate?.(30); } catch { /* */ }
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  if (!convId) return null;

  const headerName = partenaireLabel || conv?.libelle || 'Conversation';
  const hasMore = msgs?.a_plus ?? false;

  return (
    <>
    <div
      onClick={() => onFocus?.()}
      style={{
        // Empilement desktop : chaque drawer décalé horizontalement (largeur
        // 384px + gap 8px). Le style inline override sm:right-6 de Tailwind.
        // Mobile (< 640px) : le style est ignoré (le drawer prend full width).
        right: typeof window !== 'undefined' && window.innerWidth >= 640
          ? `${24 + stackIndex * 392}px`
          : undefined,
        // z-index : le focused passe au-dessus des autres pour le clic
        zIndex: 60 + (isFocused ? 5 : 0),
      }}
      className={`fixed bottom-0 right-0 sm:bottom-6 w-full sm:w-96 h-[85vh] sm:h-[560px] shadow-2xl rounded-t-xl sm:rounded-xl flex flex-col overflow-hidden border-2 transition-transform ${
        isFocused ? '' : 'sm:scale-[0.98]'
      } ${
        isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900'
      }`}
    >
      {/* Header style FB Messenger */}
      <header className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white flex-shrink-0">
        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded sm:hidden">
          <ArrowLeft size={18} />
        </button>
        <div className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center text-xs font-bold flex-shrink-0">
          {initiales(headerName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate leading-tight">{headerName}</div>
          <div className="text-[10px] text-white/80">Chat direct</div>
        </div>
        {onMinimize && (
          <button
            onClick={(e) => { e.stopPropagation(); onMinimize(); }}
            className="p-1 hover:bg-white/20 rounded hidden sm:block"
            title="Réduire (garde la conversation en bulle)"
          >
            <Minus size={16} />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="p-1 hover:bg-white/20 rounded hidden sm:block"
          title="Fermer (Esc)"
        >
          <X size={16} />
        </button>
      </header>

      {/* Messages — fix 2026-08-31 : overflow-x-hidden pour empêcher tout
          contenu long (URL, mot sans espace, citation reply_to) de créer
          une scrollbar horizontale qui gaspille l'UI du drawer. */}
      <div ref={scrollRef} className={`flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-1 ${
        isDark ? 'bg-gray-950' : 'bg-gray-50'
      }`}>
        {hasMore && (
          <div className="text-center pb-2">
            <button
              onClick={loadOlder}
              disabled={isFetching}
              className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full border transition ${
                isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
              } disabled:opacity-50`}
            >
              <ChevronUp size={12} />
              {isFetching ? 'Chargement…' : 'Charger les anciens messages'}
            </button>
          </div>
        )}
        {!historyStack.length ? (
          <div className="text-center text-xs text-gray-500 mt-8">Aucun message. Écrivez le premier ci-dessous.</div>
        ) : (
          historyStack.map((m: any, i: number) => {
            const mine = m.expediteur_user_id === myUserId;
            const prev = i > 0 ? historyStack[i - 1] : null;
            const next = i < historyStack.length - 1 ? historyStack[i + 1] : null;
            const showDate = !prev || fmtDateShort(prev.date_envoi) !== fmtDateShort(m.date_envoi);
            const showAvatar = !mine && (!next || next.expediteur_user_id !== m.expediteur_user_id);
            const groupWithPrev = prev && prev.expediteur_user_id === m.expediteur_user_id
              && !showDate;
            const menuOpen = menuOpenFor === m.id;
            const supprime = !!m.supprime;
            return (
              <React.Fragment key={m.id}>
                {showDate && (
                  <div className="text-center my-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                    }`}>{fmtDateShort(m.date_envoi)}</span>
                  </div>
                )}
                <div className={`flex ${mine ? 'justify-end' : 'justify-start'} ${groupWithPrev ? 'mt-0.5' : 'mt-2'}`}>
                  {!mine && (
                    <div className="w-7 flex-shrink-0 flex items-end">
                      {showAvatar && (
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-[9px] font-bold">
                          {initiales(m.expediteur_nom || '?')}
                        </div>
                      )}
                    </div>
                  )}
                  <div className={`max-w-[72%] ${mine ? 'ml-auto' : 'ml-1'} relative group`}>
                    {!mine && !groupWithPrev && (
                      <div className={`text-[10px] font-semibold mb-0.5 px-1 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                        {m.expediteur_nom}
                      </div>
                    )}
                    <div
                      onTouchStart={() => startLongPress(m.id, mine)}
                      onTouchEnd={cancelLongPress}
                      onTouchCancel={cancelLongPress}
                      // Fix 2026-08-31 : max-w-[85%] pour éviter que les longs
                      // messages (surtout ceux avec reply_to) débordent du
                      // drawer et créent une scrollbar horizontale.
                      className={`inline-block max-w-[85%] px-3 py-1.5 text-sm break-words whitespace-pre-wrap relative select-text ${
                        supprime
                          ? isDark
                            ? 'bg-gray-800/60 text-gray-500 italic rounded-2xl'
                            : 'bg-gray-100 text-gray-500 italic rounded-2xl border border-dashed border-gray-300'
                          : mine
                            ? 'bg-blue-600 text-white rounded-2xl rounded-br-md'
                            : isDark
                              ? 'bg-gray-800 text-gray-100 rounded-2xl rounded-bl-md'
                              : 'bg-white text-gray-900 border border-gray-200 rounded-2xl rounded-bl-md shadow-sm'
                      }`}
                    >
                      {/* Citation du message répondu (WhatsApp-style, compact)
                          Fix 2026-08-31 : line-clamp-2 + max-w-full + break-words
                          pour ne plus élargir la bulle avec de longs messages. */}
                      {!supprime && m.reply_to && (
                        <div className={`text-[11px] px-2 py-1 mb-1 rounded border-l-2 max-w-full overflow-hidden ${
                          mine
                            ? 'bg-blue-800/40 border-white/40 text-blue-100'
                            : (isDark ? 'bg-gray-900/60 border-blue-400 text-gray-300' : 'bg-gray-100 border-blue-500 text-gray-700')
                        }`}>
                          <div className="font-semibold text-[10px] opacity-90 truncate">{m.reply_to.expediteur_nom}</div>
                          <div className="opacity-80 break-words line-clamp-2" style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {m.reply_to.type_msg === 'image' ? '📷 Image'
                              : m.reply_to.type_msg === 'fichier' ? '📎 Fichier'
                              : (m.reply_to.contenu || '').slice(0, 120)}
                          </div>
                        </div>
                      )}

                      {!supprime && m.fichier_url && m.fichier_mime?.startsWith('image/') && (
                        <img
                          src={m.fichier_url}
                          alt={m.fichier_nom || 'image'}
                          className="max-w-[240px] max-h-[240px] rounded mb-1 cursor-zoom-in"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLightbox({ url: m.fichier_url, nom: m.fichier_nom });
                          }}
                        />
                      )}
                      {!supprime && m.fichier_url && m.fichier_mime?.startsWith('audio/') && (
                        <div className={`inline-flex items-center gap-1.5 mb-1 ${mine ? 'text-blue-100' : ''}`}>
                          <Mic size={12} className="flex-shrink-0" />
                          <audio src={m.fichier_url} controls preload="metadata"
                            className="h-8 max-w-[220px]" />
                        </div>
                      )}
                      {!supprime && m.fichier_url && !m.fichier_mime?.startsWith('image/')
                        && !m.fichier_mime?.startsWith('audio/') && (
                        <a href={m.fichier_url} target="_blank" rel="noreferrer"
                          className={`inline-flex items-center gap-1 mb-1 underline ${mine ? 'text-blue-100' : 'text-blue-600'}`}
                        >
                          <Paperclip size={12} />
                          {m.fichier_nom || 'Pièce jointe'}
                        </a>
                      )}
                      {supprime ? (
                        <div className="flex items-center gap-1 text-xs">
                          <Trash2 size={11} /> Message supprimé
                        </div>
                      ) : (
                        m.contenu && <div>{m.contenu}</div>
                      )}
                    </div>

                    {/* Bouton menu contextuel — visible sur hover ou long-press
                        Permet réactions rapides (tous) + suppression (mes msgs). */}
                    {!supprime && (
                      <button
                        onMouseDown={(e) => { e.stopPropagation(); }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenFor(menuOpen ? null : m.id);
                        }}
                        className={`absolute ${mine ? '-left-6' : '-right-6'} top-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${
                          isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-600 border border-gray-200 shadow-sm'
                        }`}
                        title="Options"
                      >
                        <MoreVertical size={12} />
                      </button>
                    )}

                    {/* Menu contextuel : réactions rapides + reply + transfert + supprimer */}
                    {menuOpen && (
                      <div
                        onMouseDown={(e) => e.stopPropagation()}
                        className={`absolute z-10 top-0 ${mine ? 'right-0 translate-y-full' : 'left-8'} min-w-[220px] rounded-lg shadow-xl border ${
                          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className={`flex items-center gap-1 px-2 py-1.5 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                          {['❤️', '👍', '😂', '🔥', '🎉', '🙏'].map(e => (
                            <button
                              key={e}
                              onClick={() => handleQuickReaction(m.id, e)}
                              className={`text-lg w-7 h-7 flex items-center justify-center rounded-full hover:scale-125 transition-transform ${
                                isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                              }`}
                              title={`Réagir avec ${e}`}
                            >{e}</button>
                          ))}
                        </div>
                        <button
                          onClick={() => { setReplyTo(m); setMenuOpenFor(null); }}
                          className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${
                            isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <Reply size={12} /> Répondre
                        </button>
                        <button
                          onClick={() => { setTransferSrc(m); setTransferTargets(new Set()); setMenuOpenFor(null); }}
                          className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${
                            isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <Forward size={12} /> Transférer
                        </button>
                        {mine && (
                          <button
                            onClick={() => handleDeleteMessage(m.id)}
                            className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${
                              isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-50 text-red-600'
                            }`}
                          >
                            <Trash2 size={12} />
                            Supprimer
                          </button>
                        )}
                      </div>
                    )}

                    {/* Réactions agrégées (pastilles cliquables + animation) */}
                    {Array.isArray(m.reactions) && m.reactions.length > 0 && (
                      <div className={`flex flex-wrap gap-1 mt-0.5 px-1 ${mine ? 'justify-end' : ''}`}>
                        {m.reactions.map((r: any) => (
                          <button
                            key={r.emoji}
                            onClick={() => handleQuickReaction(m.id, r.emoji)}
                            title={r.users?.map((u: any) => u.user_nom).join(', ')}
                            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] border ${
                              r.moi
                                ? (isDark ? 'bg-blue-900/40 border-blue-700 text-blue-200' : 'bg-blue-100 border-blue-300 text-blue-700')
                                : (isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-700')
                            }`}
                            style={r.moi ? { animation: 'chat-react-pop 320ms ease-out' } : undefined}
                          >
                            <span className={
                              r.emoji === '❤️' || r.emoji === '💖' ? 'chat-react-heart' :
                              r.emoji === '👍' ? 'chat-react-thumb' :
                              r.emoji === '🔥' ? 'chat-react-fire' :
                              r.emoji === '😂' || r.emoji === '🤣' ? 'chat-react-laugh' :
                              r.emoji === '🎉' || r.emoji === '🥳' ? 'chat-react-party' :
                              ''
                            }>{r.emoji}</span>
                            {r.users.length > 1 && <span className="tabular-nums">{r.users.length}</span>}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className={`text-[9px] mt-0.5 px-1 flex items-center gap-1 ${mine ? 'justify-end text-gray-500' : 'text-gray-400'}`}>
                      <span>{fmtHeure(m.date_envoi)}{m.modifie && !supprime ? ' · modifié' : ''}</span>
                      {!supprime && (
                        <ChatCheckMarks
                          mine={mine}
                          myUserId={myUserId}
                          livre_a={m.livre_a}
                          lu_par={m.lu_par}
                          isDark={isDark}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Indicateur « en train d'écrire » */}
      {typingData?.typing && typingData.typing.length > 0 && (
        <div className={`px-3 py-1 border-t text-[11px] italic ${
          isDark ? 'border-gray-800 bg-gray-900 text-gray-400' : 'border-gray-100 bg-gray-50 text-gray-500'
        }`}>
          {typingData.typing.length === 1
            ? `${typingData.typing[0].user_nom} écrit…`
            : `${typingData.typing.length} personnes écrivent…`}
        </div>
      )}

      {/* Aperçu reply */}
      {replyTo && (
        <div className={`px-3 py-2 border-t flex items-start gap-2 ${
          isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-blue-50/60'
        }`}>
          <div className="w-1 self-stretch bg-blue-500 rounded" />
          <div className="flex-1 min-w-0">
            <div className={`text-[11px] font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              Réponse à {replyTo.expediteur_nom}
            </div>
            <div className={`text-[11px] truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {replyTo.type_msg === 'image' ? '📷 Image'
                : replyTo.type_msg === 'fichier' ? '📎 Fichier'
                : (replyTo.contenu || '').slice(0, 100)}
            </div>
          </div>
          <button onClick={() => setReplyTo(null)} className="p-0.5 text-gray-400 hover:text-red-500">
            <X size={13} />
          </button>
        </div>
      )}

      {/* Aperçu image attachée */}
      {attachedPreview && (
        <div className={`px-3 py-2 border-t flex items-center gap-2 ${
          isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'
        }`}>
          <img src={attachedPreview} alt="preview" className="w-14 h-14 object-cover rounded" />
          <div className="flex-1 text-xs text-gray-600 dark:text-gray-400 truncate">
            {attachedImage?.name} — {attachedImage ? `${(attachedImage.size / 1024).toFixed(0)} Ko` : ''}
          </div>
          <button onClick={removeAttachment} className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Enregistreur vocal (remplace la barre input pendant l'enregistrement) */}
      {voiceMode ? (
        <ChatVoiceRecorder
          isDark={isDark}
          onCancel={() => setVoiceMode(false)}
          onSend={sendVoice}
        />
      ) : (
        <div className={`px-2 py-2 border-t flex items-end gap-1 ${
          isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className={`p-2 rounded-full flex-shrink-0 ${isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
            title="Joindre une image"
          >
            <ImageIcon size={18} />
          </button>
          <ChatEmojiPicker
            isDark={isDark}
            onSelect={(emoji) => setText(t => t + emoji)}
          />
          <textarea
            value={text}
            onChange={(e) => { setText(e.target.value); emitTyping(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Écrire un message… (Ctrl+V pour coller une image)"
            rows={1}
            className={`flex-1 px-3 py-2 rounded-2xl text-sm border resize-none overflow-y-auto ${
              isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-100 border-gray-200 text-gray-900'
            }`}
            style={{ maxHeight: '120px', minHeight: '38px' }}
          />
          {(!text.trim() && !attachedImage) ? (
            <button
              onClick={() => setVoiceMode(true)}
              className={`p-2 rounded-full flex-shrink-0 ${isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Message vocal"
            >
              <Mic size={18} />
            </button>
          ) : (
            <button
              onClick={send}
              disabled={sending}
              className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              title="Envoyer (Entrée)"
            >
              <Send size={16} />
            </button>
          )}
        </div>
      )}
    </div>

    {/* Modal transfert */}
    {transferSrc && (
      <div className="fixed inset-0 z-[75] bg-black/70 flex items-end sm:items-center justify-center p-3"
        onClick={() => !transferring && setTransferSrc(null)}>
        <div onClick={(e) => e.stopPropagation()}
          className={`w-full max-w-md rounded-xl shadow-2xl max-h-[85vh] flex flex-col ${
            isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
          }`}>
          <header className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div>
              <h3 className="font-semibold text-sm">Transférer le message</h3>
              <div className="text-[11px] text-gray-500 truncate max-w-[280px]">
                {transferSrc.contenu?.slice(0, 60) || transferSrc.fichier_nom || 'Média'}
              </div>
            </div>
            <button onClick={() => setTransferSrc(null)} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-2 divide-y divide-gray-100 dark:divide-gray-800">
            {(mesConvs?.results || [])
              .filter(c => c.id !== transferSrc.conversation_id)
              .map(c => {
                const sel = transferTargets.has(c.id);
                return (
                  <button key={c.id}
                    onClick={() => setTransferTargets(prev => {
                      const n = new Set(prev);
                      if (n.has(c.id)) n.delete(c.id); else n.add(c.id);
                      return n;
                    })}
                    className={`w-full p-2.5 flex items-center gap-3 text-left ${
                      isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                    } ${sel ? (isDark ? 'bg-blue-900/40' : 'bg-blue-50') : ''}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      sel ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {sel && <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 6l3 3 5-6" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>}
                    </div>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {initiales(c.titre_affichage || 'C')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.titre_affichage}</div>
                      <div className="text-[10px] text-gray-500 truncate">{c.type_conv}</div>
                    </div>
                  </button>
                );
              })}
          </div>
          <footer className={`px-4 py-3 border-t flex justify-between items-center gap-2 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <span className="text-[11px] text-gray-500">{transferTargets.size} destination(s)</span>
            <div className="flex gap-2">
              <button onClick={() => setTransferSrc(null)} disabled={transferring}
                className={`px-3 py-1.5 text-sm rounded-md ${isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Annuler
              </button>
              <button onClick={doTransfert} disabled={transferring || transferTargets.size === 0}
                className="inline-flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md font-semibold"
              >
                <Forward size={13} />
                {transferring ? 'Envoi…' : 'Transférer'}
              </button>
            </div>
          </footer>
        </div>
      </div>
    )}

    {/* Animations CSS émojis façon WhatsApp/Facebook */}
    <style>{`
      @keyframes chat-react-pop {
        0% { transform: scale(0.4); opacity: 0; }
        60% { transform: scale(1.25); opacity: 1; }
        100% { transform: scale(1); }
      }
      @keyframes chat-react-heart-beat {
        0%, 100% { transform: scale(1); }
        20% { transform: scale(1.3); }
        40% { transform: scale(0.95); }
        60% { transform: scale(1.2); }
        80% { transform: scale(1); }
      }
      @keyframes chat-react-thumb-bounce {
        0%, 100% { transform: rotate(0deg) translateY(0); }
        30% { transform: rotate(-12deg) translateY(-2px); }
        60% { transform: rotate(0deg) translateY(1px); }
      }
      @keyframes chat-react-fire-flicker {
        0%, 100% { transform: scale(1) translateY(0); filter: brightness(1); }
        50% { transform: scale(1.1) translateY(-1px); filter: brightness(1.3); }
      }
      @keyframes chat-react-laugh-shake {
        0%, 100% { transform: rotate(0deg); }
        25% { transform: rotate(-8deg); }
        50% { transform: rotate(8deg); }
        75% { transform: rotate(-4deg); }
      }
      @keyframes chat-react-party-spin {
        0% { transform: rotate(0deg) scale(1); }
        50% { transform: rotate(15deg) scale(1.15); }
        100% { transform: rotate(0deg) scale(1); }
      }
      .chat-react-heart { display: inline-block; animation: chat-react-heart-beat 1.6s ease-in-out infinite; transform-origin: center; }
      .chat-react-thumb { display: inline-block; animation: chat-react-thumb-bounce 1.4s ease-in-out infinite; transform-origin: bottom left; }
      .chat-react-fire { display: inline-block; animation: chat-react-fire-flicker 900ms ease-in-out infinite; transform-origin: bottom center; }
      .chat-react-laugh { display: inline-block; animation: chat-react-laugh-shake 1.2s ease-in-out infinite; transform-origin: center; }
      .chat-react-party { display: inline-block; animation: chat-react-party-spin 1.5s ease-in-out infinite; transform-origin: center; }
    `}</style>

    {/* ── Lightbox image plein-écran ─────────────────────────────────────── */}
    {lightbox && (
      <div
        onClick={() => setLightbox(null)}
        className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
        role="dialog"
        aria-label="Aperçu image"
      >
        {/* Barre haut */}
        <div className="absolute top-0 inset-x-0 px-3 py-3 flex items-center justify-between text-white bg-gradient-to-b from-black/80 to-transparent">
          <div className="text-sm truncate max-w-[70%]">{lightbox.nom || 'Image'}</div>
          <div className="flex items-center gap-1">
            <a
              href={lightbox.url}
              download={lightbox.nom || undefined}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noreferrer"
              className="p-2 rounded-full hover:bg-white/20"
              title="Télécharger"
            >
              <Download size={18} />
            </a>
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="p-2 rounded-full hover:bg-white/20"
              title="Fermer (Échap)"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <img
          src={lightbox.url}
          alt={lightbox.nom || 'Aperçu'}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-[92vh] object-contain rounded shadow-2xl cursor-default"
        />
      </div>
    )}
    </>
  );
};

export default PortailChatDrawer;
